import { ChevronsUpDown, LogOut } from "lucide-react";
import { Link } from "react-router";
import { ACCOUNT_SECTIONS } from "../lib/account";
import { useProfile } from "../lib/profile";
import { useSignOut } from "../lib/queries";
import { Menu, menuItemClass } from "./Menu";

/**
 * The signed-in person at the bottom of the sidebar: the account area (personal details,
 * security, profiles) and sign out. Account level, the same in every profile (the profile switcher at the
 * top is per business). `onNavigate` lets the mobile drawer close itself.
 */
export function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useProfile();
  const signOut = useSignOut();
  const initial = (user.name || user.email).charAt(0).toUpperCase();

  return (
    <Menu
      label="Account menu"
      align="left"
      side="top"
      triggerClassName="flex h-11 w-full min-w-0 items-center gap-2.5 rounded-md px-2.5 text-left hover:bg-subtle aria-expanded:bg-subtle"
      trigger={
        <>
          <span
            aria-hidden="true"
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent"
          >
            {initial}
          </span>
          <span className="min-w-0 flex-1">
            {user.name && <span className="block truncate text-[0.8125rem] font-medium">{user.name}</span>}
            <span
              className={
                user.name
                  ? "block truncate text-xs text-fg-muted"
                  : "block truncate text-[0.8125rem] font-medium"
              }
            >
              {user.email}
            </span>
          </span>
          <ChevronsUpDown aria-hidden="true" className="size-3.5 shrink-0 text-fg-subtle" />
        </>
      }
      panelClassName="w-full min-w-56"
    >
      {(close) => (
        <>
          {ACCOUNT_SECTIONS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                close(false);
                onNavigate?.();
              }}
              className={menuItemClass}
            >
              <item.icon aria-hidden="true" className="size-4 text-fg-muted" />
              {item.label}
            </Link>
          ))}
          <hr className="my-1 border-line" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
            className={menuItemClass}
          >
            <LogOut aria-hidden="true" className="size-4 text-fg-muted" />
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </button>
        </>
      )}
    </Menu>
  );
}
